import { useApiWrapper } from '../../../shared/hooks/useApi/apiWrapper';
import { createVenue, createVenueBulk, deleteVenue, resolveVenueConflicts, updateVenue } from '../api/adminApi';
import { getVenueList, getVenue } from '../../../shared/api/venueApi';
import { getVenueTagList } from '../../../shared/api/venueTagApi';
import { useAdminVenueStore } from '../store/adminVenueStore';
import {
  CreateVenueBulkItemResult,
  CreateVenueBulkResult,
  SimpleVenueData,
  VenueData,
  VenueFilterData,
} from '../../../shared/dto/EntityRepository';
import { CreateVenueBulkReq, CreateVenueReq, UpdateVenueReq } from '../../../shared/dto/ReqDtoRepository';
import { useTempAlertStore } from '../../../shared/store/portal/tempAlertStore';
import {
  isVenueDetail,
  normalizeVenueDetail,
  toUpdateVenueReq,
} from '../../component/venues/toUpdateVenueReq';
import {
  VenueConflictApplyItem,
  buildResolveVenueConflictsReq,
  buildResolvedVenue,
  buildResolvedVenueCreate,
  getVenueConflictFields,
  selectVenueConflictsToApply,
} from '../../../shared/util/venueBulk';

let latestVenueListRequestId = 0;
let latestVenueDetailRequestId = 0;
let latestMultiVenueRequestId = 0;

const invalidateVenueListRequests = () => {
  latestVenueListRequestId += 1;
};

const selectionKey = (venueIds: string[]) => venueIds.join('|');

const isCurrentMultiSelection = (venueIds: string[]) =>
  selectionKey(useAdminVenueStore.getState().multiSelectedVenueIds)
  === selectionKey(venueIds);

const normalizeVenueSummary = (venue: SimpleVenueData): SimpleVenueData => ({
  ...venue,
  id: String(venue.id),
});

const getVenueListPage = async (
  page: number,
  size: number,
  filterData: VenueFilterData,
  sortData = useAdminVenueStore.getState().sortData,
) => {
  let response = await getVenueList(
    page,
    size,
    sortData,
    filterData,
  );
  const lastAvailablePage = Math.max(1, response.data.totalPages);
  if (page > lastAvailablePage) {
    response = await getVenueList(
      lastAvailablePage,
      size,
      sortData,
      filterData,
    );
  }
  return response;
};

const applyVenueListResponse = (
  response: Awaited<ReturnType<typeof getVenueList>>,
) => {
  const content = response.data.content.map(normalizeVenueSummary);
  const current = useAdminVenueStore.getState();
  current.setVenueList(content);
  current.setPagination(response.data.page, response.data.totalPages);
  if (current.multiSelectedVenueIds.length > 0) {
    const visibleIdSet = new Set(content.map((venue) => String(venue.id)));
    const visibleSelectedIds = current.multiSelectedVenueIds.filter((id) =>
      visibleIdSet.has(id)
    );
    if (visibleSelectedIds.length !== current.multiSelectedVenueIds.length) {
      const detailById = new Map(
        current.multiSelectedVenues.map((venue) => [String(venue.id), venue]),
      );
      const visibleDetails = visibleSelectedIds.flatMap((id) => {
        const detail = detailById.get(id);
        return detail ? [detail] : [];
      });
      if (visibleSelectedIds.length > 1) {
        current.setMultiVenueSelection(visibleSelectedIds, visibleDetails);
        if (visibleDetails.length !== visibleSelectedIds.length) {
          current.setMultiVenueStatus('error');
        }
      } else if (visibleSelectedIds.length === 1) {
        current.setActiveVenue(
          visibleDetails[0]
          ?? content.find((venue) => String(venue.id) === visibleSelectedIds[0])
          ?? null,
        );
        current.setHasChanged(false);
      } else {
        current.clearVenueSelection();
      }
    }
  }
  return content;
};

const venueListContextKey = (
  page: number,
  size: number,
  filterData: VenueFilterData,
  sortData = useAdminVenueStore.getState().sortData,
) => `${page}|${size}|${JSON.stringify(filterData)}|${sortData.sort}|${sortData.direction}`;

const toBulkResult = (
  results: CreateVenueBulkItemResult[],
): CreateVenueBulkResult => {
  const sortedResults = [...results].sort((a, b) => a.rowNumber - b.rowNumber);
  return {
    totalCount: sortedResults.length,
    createdCount: sortedResults.filter((item) => item.status === 'CREATED').length,
    duplicateCount: sortedResults.filter((item) => item.status === 'SKIPPED_DUPLICATE').length,
    conflictCount: sortedResults.filter((item) => item.status === 'CONFLICT').length,
    invalidCount: sortedResults.filter((item) => item.status === 'INVALID').length,
    results: sortedResults,
  };
};

const mergeBulkResults = (
  current: CreateVenueBulkItemResult[],
  replacements: CreateVenueBulkItemResult[],
): CreateVenueBulkItemResult[] => {
  const replacementByRow = new Map(
    replacements.map((item) => [item.rowNumber, item]),
  );
  return current.map((item) => replacementByRow.get(item.rowNumber) ?? item);
};

export const useAdminVenue = () => {
  const store = useAdminVenueStore();
  const withApiHandler = useApiWrapper();
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((state) => state.setDeletedTempAlert);

  const load = async (
    page = useAdminVenueStore.getState().page,
    size = Number(useAdminVenueStore.getState().pageSize),
    filterData = useAdminVenueStore.getState().filterData,
  ): Promise<void> => {
    const requestId = ++latestVenueListRequestId;
    const sortData = useAdminVenueStore.getState().sortData;
    await withApiHandler(async () => {
      let response: Awaited<ReturnType<typeof getVenueList>>;
      try {
        response = await getVenueListPage(page, size, filterData, sortData);
      } catch (error) {
        if (requestId !== latestVenueListRequestId) return;
        throw error;
      }
      if (requestId !== latestVenueListRequestId) return;
      applyVenueListResponse(response);
    }, ['get-venue-list', page, size, filterData, sortData]);
  };

  const refreshVenueList = async (
    page: number,
    size: number,
    filterData: VenueFilterData,
  ): Promise<SimpleVenueData[] | null> => {
    const requestId = ++latestVenueListRequestId;
    const sortData = useAdminVenueStore.getState().sortData;
    const contextKey = venueListContextKey(page, size, filterData, sortData);
    const response = await getVenueListPage(page, size, filterData, sortData);
    const current = useAdminVenueStore.getState();
    if (
      requestId !== latestVenueListRequestId
      || venueListContextKey(
        current.page,
        Number(current.pageSize),
        current.filterData,
        current.sortData,
      ) !== contextKey
    ) return null;
    return applyVenueListResponse(response);
  };

  const selectVenue = async (venue: SimpleVenueData) => {
    latestMultiVenueRequestId += 1;
    const requestId = ++latestVenueDetailRequestId;
    if (!venue.id) {
      store.setHasChanged(false);
      store.setActiveVenue(venue);
      return;
    }
    const venueId = String(venue.id);
    const normalizedVenue = normalizeVenueSummary(venue);
    store.setHasChanged(false);
    store.setActiveVenue(normalizedVenue);
    await withApiHandler(async () => {
      let response: Awaited<ReturnType<typeof getVenue>>;
      try {
        response = await getVenue(venueId);
      } catch (error) {
        if (
          requestId !== latestVenueDetailRequestId
          || String(useAdminVenueStore.getState().activeVenue?.id ?? '') !== venueId
        ) return;
        throw error;
      }
      if (
        requestId !== latestVenueDetailRequestId
        || String(useAdminVenueStore.getState().activeVenue?.id ?? '') !== venueId
      ) return;
      store.setActiveVenue(normalizeVenueDetail(response.data));
    }, ['get-venue', venueId]);
  };

  const selectVenueById = async (venueId: string) => {
    latestMultiVenueRequestId += 1;
    const requestId = ++latestVenueDetailRequestId;
    store.setHasChanged(false);
    const canApply = () => {
      const current = useAdminVenueStore.getState();
      return requestId === latestVenueDetailRequestId
        && current.multiSelectedVenueIds.length === 0
        && (!current.activeVenue || String(current.activeVenue.id) === venueId);
    };
    await withApiHandler(async () => {
      let response: Awaited<ReturnType<typeof getVenue>>;
      try {
        response = await getVenue(venueId);
      } catch (error) {
        if (!canApply()) return;
        throw error;
      }
      if (!canApply()) return;
      store.setActiveVenue(normalizeVenueDetail(response.data));
    }, ['get-venue', venueId]);
  };

  const selectVenues = async (venues: SimpleVenueData[]) => {
    const uniqueVenues = venues.filter((venue, index, list) => (
      !!venue.id
      && list.findIndex((item) => String(item.id) === String(venue.id)) === index
    ));
    if (uniqueVenues.length === 0) {
      latestVenueDetailRequestId += 1;
      latestMultiVenueRequestId += 1;
      useAdminVenueStore.getState().clearVenueSelection();
      return;
    }
    if (uniqueVenues.length === 1) {
      await selectVenue(uniqueVenues[0]);
      return;
    }

    latestVenueDetailRequestId += 1;
    const requestId = ++latestMultiVenueRequestId;
    const venueIds = uniqueVenues.map((venue) => String(venue.id));
    const current = useAdminVenueStore.getState();
    const detailById = new Map<string, VenueData>();
    current.multiSelectedVenues.forEach((venue) => {
      detailById.set(String(venue.id), venue);
    });
    if (current.activeVenue && isVenueDetail(current.activeVenue)) {
      detailById.set(String(current.activeVenue.id), current.activeVenue);
    }
    const hydrated = venueIds.flatMap((id) => {
      const detail = detailById.get(id);
      return detail ? [detail] : [];
    });

    current.setMultiVenueSelection(venueIds, hydrated);
    const missingIds = venueIds.filter((id) => !detailById.has(id));
    if (missingIds.length === 0) return;

    const isCurrentRequest = () => (
      requestId === latestMultiVenueRequestId
      && isCurrentMultiSelection(venueIds)
    );
    const fetched = await withApiHandler(async () => {
      try {
        const responses = await Promise.all(missingIds.map((id) => getVenue(id)));
        return responses.map((response) => normalizeVenueDetail(response.data));
      } catch (error) {
        if (!isCurrentRequest()) return [];
        throw error;
      }
    }, ['get-selected-venues', missingIds]);

    if (!isCurrentRequest()) return;
    if (!fetched) {
      useAdminVenueStore.getState().setMultiVenueStatus('error');
      return;
    }
    fetched.forEach((venue) => detailById.set(String(venue.id), venue));
    useAdminVenueStore.getState().setMultiSelectedVenues(
      venueIds.flatMap((id) => {
        const detail = detailById.get(id);
        return detail ? [detail] : [];
      }),
    );
  };

  const create = async (data: CreateVenueReq) => {
    const createdVenue = await withApiHandler(async () => {
      const response = await createVenue(data);
      const created = normalizeVenueDetail(response.data);
      invalidateVenueListRequests();
      store.setHasChanged(false);
      store.setActiveVenue(created);
      store.setBulkResult(null);
      setUpdatedTempAlert();
      return created;
    }, ['create-venue']);
    if (!createdVenue) return;

    const { page, pageSize, filterData } = useAdminVenueStore.getState();
    await load(page, Number(pageSize), filterData);
  };

  const update = async (venueId: string, data: UpdateVenueReq): Promise<boolean> => {
    const updatedVenue = await withApiHandler(async () => {
      const response = await updateVenue(venueId, data);
      const updated = normalizeVenueDetail(response.data);
      invalidateVenueListRequests();
      store.setHasChanged(false);
      store.setActiveVenue(updated);
      setUpdatedTempAlert();
      return updated;
    }, ['update-venue', venueId]);
    if (!updatedVenue) return false;

    const { page, pageSize, filterData } = useAdminVenueStore.getState();
    await load(page, Number(pageSize), filterData);
    return true;
  };

  const remove = async (venueId: string): Promise<boolean> => {
    const targetPage = store.venueList.length === 1 && store.page > 1
      ? store.page - 1
      : store.page;
    const result = await withApiHandler(async () => {
      await deleteVenue(venueId);
      invalidateVenueListRequests();
      store.setHasChanged(false);
      store.setActiveVenue(null);
      setDeletedTempAlert();
      return true;
    }, ['delete-venue', venueId]);
    if (!result) return false;
    await load(targetPage, Number(store.pageSize));
    return true;
  };

  const updateSelectedVenueTags = async (tagIds: number[]): Promise<boolean> => {
    const current = useAdminVenueStore.getState();
    const venueIds = [...current.multiSelectedVenueIds];
    const detailById = new Map(
      current.multiSelectedVenues.map((venue) => [String(venue.id), venue]),
    );
    if (
      venueIds.length < 2
      || venueIds.some((id) => !detailById.has(id))
    ) return false;

    const uniqueTagIds = [...new Set(tagIds)];
    const page = current.page;
    const size = Number(current.pageSize);
    const filterData = current.filterData;
    const updated = await withApiHandler(async () => {
      const results = await Promise.allSettled(venueIds.map((venueId) => {
        const detail = detailById.get(venueId)!;
        return updateVenue(venueId, {
          ...toUpdateVenueReq(detail),
          tagIds: uniqueTagIds,
        });
      }));
      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      const refreshedById = new Map(detailById);
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          refreshedById.set(
            venueIds[index],
            normalizeVenueDetail(result.value.data),
          );
        }
      });

      let detailRefreshFailure: PromiseRejectedResult | undefined;
      if (failure) {
        const detailResults = await Promise.allSettled(
          venueIds.map((venueId) => getVenue(venueId)),
        );
        detailResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            refreshedById.set(
              venueIds[index],
              normalizeVenueDetail(result.value.data),
            );
          }
        });
        detailRefreshFailure = detailResults.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        );
      }

      if (isCurrentMultiSelection(venueIds)) {
        const venueStore = useAdminVenueStore.getState();
        venueStore.setMultiSelectedVenues(
          venueIds.map((venueId) => refreshedById.get(venueId)!),
        );
        venueStore.setHasChanged(false);
        if (detailRefreshFailure) venueStore.setMultiVenueStatus('error');
      }

      let listRefreshError: unknown;
      try {
        await refreshVenueList(page, size, filterData);
      } catch (error) {
        listRefreshError = error;
      }
      if (failure) throw failure.reason;
      if (detailRefreshFailure) throw detailRefreshFailure.reason;
      if (listRefreshError) throw listRefreshError;
      return true;
    }, ['update-selected-venue-tags', venueIds, uniqueTagIds], { maxRetries: 1 });
    if (!updated) return false;
    setUpdatedTempAlert();
    return true;
  };

  const removeSelectedVenues = async (): Promise<boolean> => {
    const current = useAdminVenueStore.getState();
    const venueIds = [...current.multiSelectedVenueIds];
    if (venueIds.length < 2) return false;

    const page = current.page;
    const size = Number(current.pageSize);
    const filterData = current.filterData;
    const originalDetailById = new Map(
      current.multiSelectedVenues.map((venue) => [String(venue.id), venue]),
    );

    const removed = await withApiHandler(async () => {
      const results = await Promise.allSettled(
        venueIds.map((venueId) => deleteVenue(venueId)),
      );
      const deletedIds = venueIds.filter((_, index) => results[index].status === 'fulfilled');
      const failedIds = venueIds.filter((_, index) => results[index].status === 'rejected');

      if (deletedIds.length > 0) {
        invalidateVenueListRequests();
        const venueStore = useAdminVenueStore.getState();
        const deletedIdSet = new Set(deletedIds);
        venueStore.setVenueList(
          venueStore.venueList.filter((venue) => !deletedIdSet.has(String(venue.id))),
        );

        if (isCurrentMultiSelection(venueIds)) {
          const detailResults = await Promise.allSettled(
            failedIds.map((venueId) => getVenue(venueId)),
          );
          const remainingDetails = failedIds.flatMap((venueId, index) => {
            const result = detailResults[index];
            if (result?.status === 'fulfilled') {
              return [normalizeVenueDetail(result.value.data)];
            }
            const original = originalDetailById.get(venueId);
            return original ? [original] : [];
          });
          if (failedIds.length > 1) {
            venueStore.setMultiVenueSelection(failedIds, remainingDetails);
            if (detailResults.some((result) => result.status === 'rejected')) {
              venueStore.setMultiVenueStatus('error');
            }
          } else if (failedIds.length === 1) {
            venueStore.setActiveVenue(remainingDetails[0] ?? null);
            venueStore.setHasChanged(false);
          } else {
            venueStore.clearVenueSelection();
          }
        }

        await refreshVenueList(page, size, filterData);
      }

      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (failure) throw failure.reason;
      return true;
    }, ['delete-selected-venues', venueIds], { maxRetries: 1 });
    if (!removed) return false;

    setDeletedTempAlert();
    return true;
  };

  const createBulk = async (
    data: CreateVenueBulkReq,
    invalidResults: CreateVenueBulkItemResult[] = [],
  ): Promise<boolean> => {
    if (data.venueList.length === 0) {
      store.setActiveVenue(null);
      store.setBulkItems([]);
      store.setBulkResult(toBulkResult(invalidResults));
      return true;
    }

    const response = await withApiHandler(
      () => createVenueBulk(data),
      ['create-venue-bulk'],
    );
    if (!response) return false;

    invalidateVenueListRequests();
    store.setHasChanged(false);
    store.setActiveVenue(null);
    store.setBulkItems(data.venueList);
    store.setBulkResult(toBulkResult([
      ...response.data.results,
      ...invalidResults,
    ]));
    await load(1, Number(store.pageSize));
    return true;
  };

  const resolveBulkVenueConflicts = async (
    items: VenueConflictApplyItem[],
  ): Promise<boolean> => {
    const selected = selectVenueConflictsToApply(items);
    const updates = selected.filter(
      (item): item is VenueConflictApplyItem & { venueId: number } => item.venueId != null,
    );
    const creates = selected.filter((item) => item.venueId == null);
    if (updates.length === 0 && creates.length === 0) return false;

    let applied = false;
    if (updates.length > 0) {
      const response = await withApiHandler(async () => {
        const details = await Promise.all(
          [...new Set(updates.map((item) => item.venueId))].map((venueId) =>
            getVenue(String(venueId))),
        );
        const lifecycleById = new Map(
          details.map((detail) => [Number(detail.data.id), detail.data.lifecycleStatus]),
        );
        const needsTagIds = updates.some((item) =>
          item.conflict.venueTag
          && item.choices.venueTag === 'INCOMING'
          && item.conflict.venueTag.incoming);
        const tagIdByName = new Map<string, number>();
        if (needsTagIds) {
          const tags = await getVenueTagList();
          tags.data.forEach((tag) => tagIdByName.set(tag.name, tag.id));
        }
        return resolveVenueConflicts(buildResolveVenueConflictsReq(
          updates.map((item) => {
            const lifecycleStatus = lifecycleById.get(item.venueId);
            if (!lifecycleStatus) {
              throw new Error(`Venue ${item.venueId} was not found`);
            }
            return {
              venueId: item.venueId,
              venueCode: item.venueCode,
              venue: buildResolvedVenue(
                item.incoming,
                item.conflict,
                item.choices,
                lifecycleStatus,
                tagIdByName,
              ),
            };
          }),
        ));
      }, ['resolve-venue-conflicts']);
      if (!response) return false;

      const updatedIds = new Set(
        response.data.results
          .filter((item) => item.status === 'UPDATED')
          .map((item) => item.venueId),
      );
      const invalidMessageById = new Map(
        response.data.results
          .filter((item) => item.status === 'INVALID')
          .map((item) => [item.venueId, item.message]),
      );
      const sentRowNumbers = new Set(updates.map((item) => item.rowNumber));
      const latest = useAdminVenueStore.getState();
      latest.setBulkResult(toBulkResult(
        (latest.bulkResult?.results ?? []).map((item) => {
          if (!sentRowNumbers.has(item.rowNumber) || item.venueId == null) return item;
          if (updatedIds.has(item.venueId)) {
            return {
              ...item,
              status: 'UPDATED',
              message: null,
              venueConflict: undefined,
            };
          }
          if (invalidMessageById.has(item.venueId)) {
            return {
              ...item,
              message: invalidMessageById.get(item.venueId) ?? item.message,
            };
          }
          return item;
        }),
      ));
      if (updatedIds.size > 0) {
        invalidateVenueListRequests();
        await load(latest.page, Number(latest.pageSize));
      }
      applied = true;
    }

    if (creates.length > 0) {
      let venueList: { rowNumber: number; venue: CreateVenueReq }[];
      try {
        venueList = creates.map((item) => ({
          rowNumber: item.rowNumber,
          venue: buildResolvedVenueCreate(item.incoming, item.conflict, item.choices),
        }));
      } catch {
        return applied;
      }
      const response = await withApiHandler(
        () => createVenueBulk({ venueList }),
        ['retry-venue-conflict'],
      );
      if (!response) return applied;

      const latest = useAdminVenueStore.getState();
      const createdByRow = new Map(venueList.map((item) => [item.rowNumber, item]));
      latest.setBulkItems(latest.bulkItems.map((item) =>
        createdByRow.get(item.rowNumber) ?? item));
      const createdCodes = new Set(
        response.data.results
          .filter((item) => item.status === 'CREATED' && item.venueCode)
          .map((item) => item.venueCode!.toUpperCase()),
      );
      const merged = mergeBulkResults(
        latest.bulkResult?.results ?? [],
        response.data.results,
      ).map((item) => {
        if (
          item.status !== 'CONFLICT'
          || !item.venueCode
          || !createdCodes.has(item.venueCode.toUpperCase())
          || (item.venueConflict
            && getVenueConflictFields(item.venueConflict).length > 0)
        ) {
          return item;
        }
        const created = response.data.results.find((result) =>
          result.status === 'CREATED'
          && result.venueCode?.toUpperCase() === item.venueCode?.toUpperCase());
        return {
          ...item,
          status: 'SKIPPED_DUPLICATE' as const,
          venueId: created?.venueId ?? item.venueId,
          message: 'venueCode was created from another row',
          venueConflict: undefined,
        };
      });
      latest.setBulkResult(toBulkResult(merged));
      if (response.data.results.some((item) => item.status === 'CREATED')) {
        invalidateVenueListRequests();
        await load(
          useAdminVenueStore.getState().page,
          Number(useAdminVenueStore.getState().pageSize),
        );
      }
      applied = true;
    }

    return applied;
  };

  return {
    ...store,
    load,
    selectVenue,
    selectVenueById,
    selectVenues,
    create,
    update,
    remove,
    updateSelectedVenueTags,
    removeSelectedVenues,
    createBulk,
    resolveBulkVenueConflicts,
  };
};
